import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Box,
  Heading,
  Button,
  Card,
  CardBody,
  Text,
  Badge,
  Divider,
  BankIcon,
  EditIcon,
  LockIcon,
  Alert,
  Tooltip,
  TooltipInteractiveWrapper,
  Spinner,
} from '@razorpay/blade/components';
import { routePaths } from 'components/Routes/data';
import { ChangeAccountConfirmationModal } from './ChangeAccountConfirmationModal';
import { NoBankAccountCard } from './NoBankAccountCard';
import { AccountHistory } from './AccountHistory';
import { prepareUrlParams } from 'utils/Urls';
import { useUserId } from '../hooks/useUserId';
import { ErrorView } from 'components/ui/ErrorView/ErrorView';
import { useFormWithProvider } from 'forms';
import { useSelector } from 'react-redux';
import loggedInUserSelectors from 'reducers/loggedInUser/selectors';
import { useGetBankAccountDetails } from '../hooks/useGetBankAccountDetails';
import { unverifiedStatuses, getStatusBadgeProps } from './utils';
import { bankAccountApprovalStatus, bankFormDataInitialState } from '../data';
import { BankAccountFormData } from '../AddEditBankAccount/types';

export const PreviewBankAccount = () => {
  const { isEditLimitExceeded, latestBankAccount, accountHistory, queryResponse } =
    useGetBankAccountDetails();
  const [showChangeAccountModal, setShowChangeAccountModal] = useState(false);
  const history = useHistory();
  const userId = useUserId();
  const { setFormData } = useFormWithProvider<BankAccountFormData>();
  const isRxCurrentAccountMerchant = !!useSelector(loggedInUserSelectors.currentOrganization)
    ?.isRxCurrentAccountMerchant;

  const isAccountUnverified = latestBankAccount?.verification_status
    ? unverifiedStatuses.includes(latestBankAccount?.verification_status)
    : true;

  const shouldVerifyAccount = isAccountUnverified && isRxCurrentAccountMerchant;

  const moveToEditPage = (params: { userId?: number }) => {
    history.push({
      pathname: routePaths.employeeDetailsV2.bankAccountDetails.edit,
      search: prepareUrlParams(params),
    });
  };

  const verifyOrEditAccount = () => {
    if (shouldVerifyAccount) {
      setFormData({
        accountNumber: latestBankAccount?.account_number || bankFormDataInitialState.accountNumber,
        ifscCode: latestBankAccount?.ifsc || bankFormDataInitialState.ifscCode,
        confirmAccountNumber:
          latestBankAccount?.account_number || bankFormDataInitialState.confirmAccountNumber,
        bankProofType: bankFormDataInitialState.bankProofType,
      });
      moveToEditPage({
        userId,
      });
    } else {
      setShowChangeAccountModal(true);
    }
  };

  const isDataFetching = queryResponse.status === 'loading';
  const isDataFetchingError = queryResponse.status === 'error';
  const showActiveAccount = !!latestBankAccount;
  const showAccountHistory = !!accountHistory?.length;
  const showNoAccountCard = !showActiveAccount && !showAccountHistory;

  const buttonLabel = shouldVerifyAccount ? 'Verify Account' : 'Change Account';
  const ButtonIcon = shouldVerifyAccount ? undefined : EditIcon;

  const badgeProps = getStatusBadgeProps(isRxCurrentAccountMerchant, latestBankAccount);

  const isButtonDisabled =
    latestBankAccount?.approval_status === bankAccountApprovalStatus.pending || isEditLimitExceeded;

  const buttonTooltipContent = isEditLimitExceeded
    ? 'Bank update limit reached. Please contact your admin for further changes.'
    : latestBankAccount?.approval_status === bankAccountApprovalStatus.pending
    ? 'Bank account is already in pending approval. Please wait for approval.'
    : '';

  const renderContent = () => {
    if (isDataFetching) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" height="100%">
          <Spinner accessibilityLabel="loading bank account" />
        </Box>
      );
    }

    if (isDataFetchingError) {
      return <ErrorView onRefetch={queryResponse.refetch} />;
    }

    if (showNoAccountCard) {
      return <NoBankAccountCard />;
    }

    return (
      <>
        {showActiveAccount ? (
          <>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              marginBottom="spacing.5">
              <Heading size="small" weight="semibold" color="surface.text.gray.normal">
                Active Bank Account
              </Heading>
              {isButtonDisabled ? (
                <Tooltip content={buttonTooltipContent}>
                  <TooltipInteractiveWrapper>
                    <Button icon={LockIcon} size="small" variant="secondary" isDisabled>
                      Change Account
                    </Button>
                  </TooltipInteractiveWrapper>
                </Tooltip>
              ) : (
                <Button
                  size="small"
                  variant="secondary"
                  icon={ButtonIcon}
                  iconPosition="left"
                  onClick={verifyOrEditAccount}>
                  {buttonLabel}
                </Button>
              )}
            </Box>
            {latestBankAccount.approval_status === bankAccountApprovalStatus.pending && (
              <Alert
                marginBottom="spacing.5"
                title="Approval Pending"
                description="Your bank account details will be updated after admin approval. Until then, your current bank account details will be used for payments."
                color="information"
                isDismissible={false}
              />
            )}
            <Card padding="spacing.5" marginBottom="spacing.5" elevation="none">
              <CardBody>
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  marginBottom="spacing.3">
                  <BankIcon size="medium" color="surface.icon.gray.normal" />
                  {badgeProps ? (
                    <Badge size="small" color={badgeProps.color} emphasis="subtle">
                      {badgeProps.label}
                    </Badge>
                  ) : null}
                </Box>
                <Text marginTop="spacing.5" marginBottom="spacing.3" weight="semibold">
                  {latestBankAccount.account_number}
                </Text>
                <Text color="surface.text.gray.muted">IFSC Code: {latestBankAccount.ifsc}</Text>
              </CardBody>
            </Card>
          </>
        ) : (
          <NoBankAccountCard height="initial" />
        )}

        {showAccountHistory && <Divider thickness="thin" marginY="spacing.8" />}

        {showAccountHistory && <AccountHistory accountHistory={accountHistory} />}
      </>
    );
  };

  return (
    <Box maxWidth="550px" margin="auto" padding={['spacing.8', 'spacing.5']}>
      <Heading
        marginBottom="spacing.9"
        size="large"
        weight="semibold"
        color="surface.text.gray.normal">
        Bank Account Details
      </Heading>

      {renderContent()}

      <ChangeAccountConfirmationModal
        isOpen={showChangeAccountModal}
        onDismiss={() => {
          setShowChangeAccountModal(false);
        }}
        onContinue={() => moveToEditPage({ userId })}
      />
    </Box>
  );
};
